import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { shiftMonth, todayDayIndex, localDateFromDay } from '@betreuung/shared';
import { useAppData } from './appData';

/**
 * Tab and view state.
 *
 * There is no router on purpose: the app is an installed, single-screen PWA with
 * no deep links, and the state that has to survive a tab switch (visible month,
 * statistics period, list scroll positions) would have to live outside the router
 * anyway. Everything navigational goes through this one context, so introducing
 * a router later is a contained change.
 */

export type Tab = 'current' | 'month' | 'year' | 'stats' | 'changes';

export const TAB_ORDER: readonly Tab[] = ['current', 'month', 'year', 'stats', 'changes'];

export interface StatsPeriod {
  mode: 'month' | 'year';
  year: number;
  /** Only meaningful when `mode` is `month`. */
  month: number;
}

interface NavigationValue {
  tab: Tab;
  setTab: (tab: Tab) => void;

  monthCursor: { year: number; month: number };
  setMonthCursor: (cursor: { year: number; month: number }) => void;
  stepMonth: (delta: number) => void;
  goToToday: () => void;

  yearCursor: number;
  stepYear: (delta: number) => void;

  statsPeriod: StatsPeriod;
  setStatsPeriod: (period: StatsPeriod) => void;

  /** Jumping from the year overview into a specific month. */
  goToMonth: (year: number, month: number) => void;

  adminOpen: boolean;
  setAdminOpen: (open: boolean) => void;
}

const NavigationContext = createContext<NavigationValue | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const today = useMemo(() => localDateFromDay(todayDayIndex()), []);
  const [tab, setTab] = useState<Tab>('current');
  const [monthCursor, setMonthCursor] = useState({ year: today.year, month: today.month });
  const [yearCursor, setYearCursor] = useState(today.year);
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>({
    mode: 'month',
    year: today.year,
    month: today.month,
  });
  const [adminOpen, setAdminOpen] = useState(false);

  const stepMonth = useCallback((delta: number) => {
    setMonthCursor((current) => shiftMonth(current.year, current.month, delta));
  }, []);

  const goToToday = useCallback(() => {
    const now = localDateFromDay(todayDayIndex());
    setMonthCursor({ year: now.year, month: now.month });
  }, []);

  const stepYear = useCallback((delta: number) => {
    setYearCursor((current) => current + delta);
  }, []);

  const goToMonth = useCallback((year: number, month: number) => {
    setMonthCursor({ year, month });
    setTab('month');
  }, []);

  const value = useMemo<NavigationValue>(
    () => ({
      tab,
      setTab,
      monthCursor,
      setMonthCursor,
      stepMonth,
      goToToday,
      yearCursor,
      stepYear,
      statsPeriod,
      setStatsPeriod,
      goToMonth,
      adminOpen,
      setAdminOpen,
    }),
    [tab, monthCursor, stepMonth, goToToday, yearCursor, stepYear, statsPeriod, goToMonth, adminOpen],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationValue {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigation must be used inside NavigationProvider');
  return context;
}

/**
 * The tabs this account actually gets, and which of them is showing.
 *
 * A view-only account has no "Änderungen": the log records who changed what and
 * when, including accounts being granted and admin rights being handed out, and
 * that is the household's own record rather than something a guest needs in
 * order to see how care time is divided. The endpoint stays open — this is scope,
 * not a security boundary.
 *
 * Lives here rather than in the context because the provider sits above
 * AppDataProvider and so cannot see who is signed in.
 */
export function useVisibleTabs(): { tabs: readonly Tab[]; activeTab: Tab } {
  const { canWrite } = useAppData();
  const { tab } = useNavigation();

  const tabs = useMemo(
    () => (canWrite ? TAB_ORDER : TAB_ORDER.filter((value) => value !== 'changes')),
    [canWrite],
  );

  // Guards the case where a tab disappears under a session whose role changed.
  return { tabs, activeTab: tabs.includes(tab) ? tab : 'current' };
}
